import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import SettingsIcon from '@material-ui/icons/Settings';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import AssessmentIcon from '@material-ui/icons/Assessment';
import DevicesOtherIcon from '@material-ui/icons/DevicesOther';
import ChatIcon from '@material-ui/icons/Chat';
import { useAdminAccess } from '@internal/plugin-admin';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const { isAdmin, pluginAccess, loading, error } = useAdminAccess();

  // Fail-open: show all items during loading or on error
  const showAll = loading || !!error;
  const canSee = (pluginName: string) =>
    showAll || pluginAccess[pluginName] !== false;

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={HomeIcon} to="catalog" text="Home" />
          <MyGroupsSidebarItem
            singularTitle="My Group"
            pluralTitle="My Groups"
            icon={GroupIcon}
          />
          <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
          <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
          {canSee('cost-insight') && (
            <SidebarItem icon={ExtensionIcon} to="cost-insight" text="Costs" />
          )}
          {canSee('metrics') && (
            <SidebarItem icon={AssessmentIcon} to="metrics" text="Metrics" />
          )}
          {canSee('fleet-insight') && (
            <SidebarItem icon={DevicesOtherIcon} to="fleet-insight" text="Fleet" />
          )}
          {canSee('ai-assistant') && (
            <SidebarItem icon={ChatIcon} to="ai-assistant" text="Assistant" />
          )}
          {canSee('mattaverse-orchestration') && (
            <SidebarItem
              icon={DevicesOtherIcon}
              to="mattaverse-orchestration"
              text="Mattaverse Orchestration"
            />
          )}
          {canSee('test-equipments') && (
            <SidebarItem
              icon={DevicesOtherIcon}
              to="test-equipments"
              text="Test Equipments"
            />
          )}
          <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
          {/* End global nav */}
          <SidebarDivider />
          {(showAll || isAdmin) && (
            <SidebarItem icon={SettingsIcon} to="admin" text="Admin" />
          )}
          <SidebarScrollWrapper>
            {/* Items in this group will be scrollable if they run out of space */}
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
